import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  Button,
  Flex,
  Image,
  Box,
  Stack,
} from "@chakra-ui/react";
import { CheckoutProgress, Pedido, Pix } from "../../utils/interfaces";
import { formatBRL } from "../../utils/currency";
import { useCallback, useEffect, useState } from "react";
import { useMutationPix } from "../../hooks/pagamento/mutationPix";
import { AxiosErrorResponse } from "../../hooks/pdv/mutationReserva";
import Swal from "sweetalert2";
import { MdPix } from "react-icons/md";
import { api } from "../../libs/axios";
import { PixTimerFooter } from '../../components/Modal/PixTimerFooter';
import { useCheckoutStatus } from "../../hooks/pagamento/useCheckoutStatus";
import { CheckoutProgressPanel, CheckoutStayOnPageWarning } from "../../components/CheckoutProgress";
import { useRef } from "react";
import { createCheckoutProgressOverride, mergeCheckoutProgress } from "../../utils/checkoutProgress";
import { useVisualCheckoutProgress } from "../../hooks/pagamento/useVisualCheckoutProgress";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  pedido: Pedido;
  isPaymentExpired?: boolean;
}


const sanitizeCheckoutErrorMessage = (rawMessage?: string) => {
  const base = (rawMessage ?? 'Erro ao finalizar o pedido').trim();
  const cleaned = base.replace(/\s*-+\s*Request ID:.*/i, '').trim();

  if (/erro ao criar pix|erro ao criar cobrança pix|curl error|openssl|api\.sicoob\.com\.br\/pix/i.test(cleaned)) {
    return 'Erro ao criar PIX!\nTente novamente!';
  }

  if (/genericexception|buson|does not contain desired reserve|falha na confirmacao|confirmorderreserve|nao e possivel ler o retorno/i.test(cleaned.toLowerCase())) {
    return 'Não é possível confirmar uma ou mais poltronas.\nO pedido e o pagamento foram cancelados.';
  }

  return cleaned;
};

const isCreatePixErrorMessage = (message: string) => (
  message.trim() === 'Erro ao criar PIX!\nTente novamente!'
);

const showPixError = (message: string, onConfirm?: () => void) => {
  const isCreatePixError = isCreatePixErrorMessage(message);

  Swal.fire({
    icon: "error",
    titleText: isCreatePixError ? message : "Erro ao finalizar o pedido",
    text: isCreatePixError ? undefined : message,
    position: "center",
    showConfirmButton: true,
    confirmButtonText: "OK",
    didOpen: () => {
      const titleElement = Swal.getTitle();
      if (titleElement) {
        titleElement.style.whiteSpace = 'pre-line';
      }
    },
  }).then((result) => {
    if (result.isConfirmed) {
      onConfirm?.();
    }
  });
};


export function PagamentoPix({ isOpen, onClose, pedido, isPaymentExpired = false }: Props){
  const [pix, setPix] = useState<Pix | null>(null);
  const [localProgress, setLocalProgress] = useState<ReturnType<typeof createCheckoutProgressOverride> | null>(null);
  const errorHandledRef = useRef(false);
  const completionHandledRef = useRef(false);

  const mutation = useMutationPix(pedido.id);
  const { data: checkoutProgress, refetch } = useCheckoutStatus(pedido.id, isOpen && !isPaymentExpired);
  const pixCheckoutProgress = checkoutProgress?.method === "pix" ? checkoutProgress : undefined;

  const fetchPixStatus = async (id: string) => {
    const response = await api.get(`/checkout/pix/consultar/${id}`);

    return response.data;
  }

  useEffect(() => {
    const gerarPix = async () => {
      if (isPaymentExpired) {
        return;
      }

      if(pedido.pix === null){
        try {
          setLocalProgress(
            createCheckoutProgressOverride(
              pedido.id,
              "pix",
              "generating_pix",
              "Gerando cobrança PIX",
              "Estamos gerando o QR Code e preparando a cobrança para pagamento.",
            ),
          );
          const pix = await mutation.mutateAsync()
  
          setPix(pix)
          setLocalProgress(
            createCheckoutProgressOverride(
              pedido.id,
              "pix",
              "waiting_payment",
              "Aguardando pagamento",
              "PIX gerado com sucesso. Agora estamos aguardando a confirmação do pagamento.",
            ),
          );
        }catch (err){
          setLocalProgress(null);
          const error = err as AxiosErrorResponse

          const message = sanitizeCheckoutErrorMessage(
            error.response?.data?.data?.message ||
            error.response?.data?.message ||
            'Erro desconhecido'
          );
          const redirectTo = error.response?.data?.data?.redirect_to;

          if (redirectTo) {
            onClose();
            sessionStorage.setItem('gopass_redirect_error_message', message);
            window.location.href = redirectTo;
            return;
          }

          showPixError(message);

          onClose()
        }
      }else {
        setPix(pedido.pix)
        setLocalProgress(
          createCheckoutProgressOverride(
            pedido.id,
            "pix",
            "waiting_payment",
            "Aguardando pagamento",
            "PIX já gerado. Agora estamos aguardando a confirmação do pagamento.",
          ),
        );
      }
    }

    if(isOpen){
      gerarPix()
    } 
  }, [pedido, isOpen, isPaymentExpired])

  useEffect(() => {
    let interval: ReturnType<typeof setTimeout>;

    const verificarPagamento = async () => {
      try {
        await fetchPixStatus(pedido.id);
        setLocalProgress(
          createCheckoutProgressOverride(
            pedido.id,
            "pix",
            "payment_registered",
            "Pagamento registrado",
            "Pagamento confirmado com sucesso. Agora vamos confirmar o pedido.",
          ),
        );
        clearInterval(interval);
        await refetch();
      }catch (err: unknown) {
        const error = err as AxiosErrorResponse

        if(error.response?.status === 422){
          const message = sanitizeCheckoutErrorMessage(
            error.response?.data?.data?.message ||
            error.response?.data?.message ||
            'Erro ao finalizar o pedido'
          );
          const redirectTo = error.response?.data?.data?.redirect_to;

          if (redirectTo) {
            clearInterval(interval)
            onClose();
            sessionStorage.setItem('gopass_redirect_error_message', message);
            window.location.href = redirectTo;
            return;
          }

          showPixError(message, () => window.location.reload());
          
          clearInterval(interval)
        }

      }
    };

    if (isOpen && !isPaymentExpired) {
      interval = setInterval(verificarPagamento, 15000); // Executa a cada 15 segundos
    }

    return () => {
      clearInterval(interval); // Limpa o intervalo ao desmontar o componente
    };
  }, [isOpen, pedido.id, onClose, isPaymentExpired]);

  useEffect(() => {
    if (!pixCheckoutProgress || !isOpen) {
      return;
    }

    if (pixCheckoutProgress.status === "failed" && !errorHandledRef.current) {
      errorHandledRef.current = true;
      completionHandledRef.current = true;
      setLocalProgress(null);
      const message = pixCheckoutProgress.error_message || pixCheckoutProgress.description || 'Erro ao finalizar o pedido';

      showPixError(message, () => {
        if (pixCheckoutProgress.redirect_to) {
          sessionStorage.setItem('gopass_redirect_error_message', message);
          window.location.href = pixCheckoutProgress.redirect_to;
          return;
        }

        window.location.reload();
      });
    }
  }, [pixCheckoutProgress, isOpen, onClose, pedido.id]);

  useEffect(() => {
    if (!isOpen) {
      errorHandledRef.current = false;
      completionHandledRef.current = false;
      setLocalProgress(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (pixCheckoutProgress?.step && !["generating_pix", "waiting_payment", "payment_registered"].includes(pixCheckoutProgress.step)) {
      setLocalProgress(null);
    }
  }, [pixCheckoutProgress?.step]);

  const handleCompleted = useCallback((progress: CheckoutProgress) => {
    if (completionHandledRef.current) {
      return;
    }

    completionHandledRef.current = true;
    const target = progress.redirect_to || `/finalizada/${pedido.id}`;
    window.location.assign(target);
  }, [pedido.id]);

  const effectiveProgress = useVisualCheckoutProgress({
    pedidoId: pedido.id,
    method: "pix",
    remoteProgress: pixCheckoutProgress,
    localProgress,
    enabled: isOpen && !isPaymentExpired,
    onCompleted: handleCompleted,
  }) ?? mergeCheckoutProgress(pixCheckoutProgress, localProgress);

  return (
    <Modal
      size={"5xl"}
      key={"pagamento-dinheiro"}
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay />
        <ModalContent bg={'#f4f4f4'} maxW="1120px" borderRadius="2xl">
        <ModalHeader>PAGAMENTO PIX - DETALHES DO PAGAMENTO</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Stack spacing={6} mt={4}>
            <Box
              w="100%"
            >
              <CheckoutStayOnPageWarning />
            </Box>

            <Box
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="2xl"
              px={{ base: 4, md: 6 }}
              py={{ base: 4, md: 5 }}
              boxShadow="sm"
            >
              <Stack spacing={6} align="center">
                <Box textAlign="center">
                  <Text fontSize="xs" fontWeight="700" color="gray.500" letterSpacing="0.08em">
                    VALOR TOTAL DO PEDIDO
                  </Text>
                  <Text mt={1} fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold" color="purple.900">
                    {formatBRL(Number(pedido.valor_total))}
                  </Text>
                </Box>

                <Box
                  w="100%"
                  maxW="360px"
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="2xl"
                  px={5}
                  py={5}
                  boxShadow="sm"
                >
                  <Flex gap={3} flexDir={'column'} alignItems={'center'} justifyContent={'center'}>
                    {pix && (
                      <Image
                        src={pix.qrCode}
                        maxW="240px"
                        w="100%"
                        bg="white"
                        p={3}
                        borderRadius="xl"
                        border="1px solid"
                        borderColor="gray.200"
                      />
                    )}
                    <Button
                      mt={1}
                      leftIcon={<MdPix />}
                      isDisabled={isPaymentExpired}
                      onClick={async () => {
                        await navigator.clipboard.writeText(pix?.dados_qrcode ?? '')
                        Swal.fire({
                          icon:'success',
                          title: 'Sucesso',
                          text: 'O PIX foi copiado para área de transferência',
                        })
                      }}
                      w="100%"
                      size="lg"
                      bg="green.500"
                      color="white"
                      _hover={{ bg: "green.600" }}
                    >
                      Copiar PIX Copia e Cola
                    </Button>
                  </Flex>
                </Box>

                <Box w="100%" maxW="760px">
                  <Text fontSize="xs" fontWeight="700" color="gray.500" letterSpacing="0.08em" textAlign="center">
                    CÓDIGO PIX COPIA E COLA
                  </Text>
                  <Text mt={1} fontSize="sm" color="gray.500" textAlign="center">
                    Use este código apenas se preferir colar o PIX no app do banco.
                  </Text>
                  <Box
                    mt={3}
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="xl"
                    px={{ base: 4, md: 5 }}
                    py={{ base: 4, md: 4 }}
                  >
                    <Text
                      fontSize="sm"
                      lineHeight="1.8"
                      color="gray.700"
                      textAlign="left"
                      maxW={"100%"}
                      wordBreak={"break-all"}
                      whiteSpace={"normal"}
                      overflowWrap={"anywhere"}
                    >
                      {pix?.dados_qrcode}
                    </Text>
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Box w={"100%"}>
            <CheckoutProgressPanel progress={effectiveProgress} fallbackStep="waiting_payment" method="pix" />
            </Box>
          </Stack>
        </ModalBody>
        <PixTimerFooter 
          isOpen={isOpen}
          dataExpiracao={pix?.data_expiracao ?? ""}
        />
      </ModalContent>
    </Modal>
  );
}
